import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  type OnDestroy,
} from '@angular/core';
import { pendingUntilEvent } from '@angular/core/rxjs-interop';
import { ɵAngularFireSchedulers } from '@angular/fire';
import { MatIconRegistry } from '@angular/material/icon';
import {
  DomSanitizer,
  Title,
  type SafeResourceUrl,
} from '@angular/platform-browser';
import { AuthService } from '@principle-theorem/ng-auth';
import {
  IntercomCustomData,
  IntercomService,
  type IIntercomUser,
} from '@principle-theorem/ng-intercom';
import { HotkeysService } from '@principle-theorem/ng-principle';
import { PartnerService } from '@principle-theorem/ng-principle-shared';
import {
  APP_VERSION,
  AppVersionService,
  BreadcrumbsStore,
  DatabaseUsageTrackerService,
  DynamicTitleService,
} from '@principle-theorem/ng-shared';
import {
  PRINCIPLE_CORE_SERIALISERS,
  RootConfig,
} from '@principle-theorem/principle-core';
import {
  AppName,
  IPartner,
  type IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  FUNCTIONS_REGION,
  FirebaseFunctionsScheduler,
  Firestore,
  FirestoreScheduler,
  SerialisationProvider,
  filterUndefined,
  slugify,
  type WithRef,
} from '@principle-theorem/shared';
import { type IntercomBootInput } from '@supy-io/ngx-intercom';
import { getApp } from 'firebase/app';
import { initializeFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { isEqual } from 'lodash';
import * as LogRocket from 'logrocket';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';

/**
 * This should be mirrored in:
 * - apps/principle/src/app/app.component.ts
 * - apps/principle-functions/src/app/initialiseApp.ts
 * - apps/tinker/src/app/initialise-app.ts
 * - apps/principle-migrations/src/app/initialise-app.ts
 */
export const PRINCIPLE_SERIALISERS = PRINCIPLE_CORE_SERIALISERS;

const INTERCOM_BOOT_SETTINGS: IntercomBootInput = {
  custom_launcher_selector: '#intercom-launcher',
  hide_default_launcher: true,
};

interface IPrincipleIntercomCustomData extends IntercomCustomData {
  workspace: string;
  workspace_slug: string;
  brand_uid?: string;
  brand_slug?: string;
  practice_uid?: string;
  practice_slug?: string;
}

@Component({
  selector: 'pr-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [BreadcrumbsStore],
})
export class AppComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();

  constructor(
    private _iconRegistry: MatIconRegistry,
    private _domSanitizer: DomSanitizer,
    dynamicTitle: DynamicTitleService,
    title: Title,
    partnerService: PartnerService,
    intercom: IntercomService,
    versionService: AppVersionService,
    private _hotkeysService: HotkeysService,
    private _authService: AuthService,
    usageTracker: DatabaseUsageTrackerService,
    @Inject(APP_VERSION) appVersion: string,
    public breadcrumbs: BreadcrumbsStore,
    injector: Injector
  ) {
    // eslint-disable-next-line no-console
    console.log(`Running version: ${appVersion}`);
    this._registerIcons([
      { label: 'principle-watermark', file: 'principle-watermark-logo.svg' },
      { label: 'principle-logo', file: 'principle-logo.svg' },
      { label: 'principle-row-white', file: 'principle-row-white.svg' },
      { label: 'principle-stacked-white', file: 'principle-stacked-white.svg' },
    ]);
    this._registerSvgIconSets([`symbol-defs.svg`]);
    this._iconRegistry.registerFontClassAlias(
      'material-symbols',
      'material-symbols-outlined'
    );

    dynamicTitle.default = 'Principle Partner Portal';
    dynamicTitle.title$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value: string) => title.setTitle(value));

    if (environment.useEmulator) {
      connectFunctionsEmulator(getFunctions(), 'localhost', 5001);
      initializeFirestore(getApp(), {
        experimentalForceLongPolling: true,
        host: 'localhost:8080',
        ssl: false,
      });
    } else {
      intercom.boot(INTERCOM_BOOT_SETTINGS);

      combineLatest([partnerService.user$, partnerService.partner$])
        .pipe(
          map(([user, partner]) => this._toIntercomUser(user, partner)),
          distinctUntilChanged(isEqual),
          takeUntil(this._onDestroy$)
        )
        .subscribe((user?: IIntercomUser) =>
          intercom.updateUser(user, INTERCOM_BOOT_SETTINGS)
        );

      combineLatest([
        partnerService.user$.pipe(filterUndefined()),
        partnerService.partner$.pipe(filterUndefined()),
      ])
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(([user, partner]) => {
          LogRocket.identify(user.ref.path, {
            name: user.name,
            email: user.email,
            partner: partner.name,
          });
        });
    }

    const scheduler = new ɵAngularFireSchedulers();

    FirestoreScheduler.init(
      scheduler.outsideAngular,
      pendingUntilEvent(injector),
      getApp().name,
      environment.firebase
    );

    FirebaseFunctionsScheduler.init(
      scheduler.outsideAngular,
      FUNCTIONS_REGION,
      environment.firebase,
      environment.useEmulator ? ['localhost', 5001] : false
    );

    SerialisationProvider.init(PRINCIPLE_SERIALISERS);

    this._hotkeysService.registerHotkeys();

    if (environment.production) {
      const public$ = Firestore.doc$(RootConfig.public()).pipe(
        map((config) => config.versions?.[AppName.Partners]),
        filter((version): version is string => !!version)
      );

      const logoutFn = (): Promise<void> => this._authService.logout();
      versionService.watchForUpdates(public$, logoutFn);
    }

    usageTracker.track();
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  private _toIntercomUser(
    user?: WithRef<IUser>,
    partner?: WithRef<IPartner>
  ): IIntercomUser | undefined {
    if (!user || !partner) {
      return undefined;
    }

    const customData: IPrincipleIntercomCustomData = {
      workspace: partner.name,
      workspace_uid: partner.ref.id,
      workspace_slug: slugify(partner.name),
    };

    return {
      name: user.name,
      email: user.email,
      company: {
        company_id: partner.ref.id,
        name: partner.name,
      },
      createdAt: user.createdAt.toDate(),
      customData,
    };
  }

  private _registerIcons(icons: IIconAsset[]): void {
    icons.map((icon: IIconAsset) => {
      const path: SafeResourceUrl =
        this._domSanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/${icon.file}`
        );
      this._iconRegistry.addSvgIconInNamespace('assets', icon.label, path);
    });
  }

  private _registerSvgIconSets(iconSetFiles: string[]): void {
    iconSetFiles.map((file: string) => {
      const path: SafeResourceUrl =
        this._domSanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/${file}`
        );
      this._iconRegistry.addSvgIconSet(path);
    });
  }
}

interface IIconAsset {
  label: string;
  file: string;
}
